/** Week: seven-day grid with completion, the week's sessions and food, and the
 *  trajectory target against the actual 7-day mean. Section 7. */
import { useState } from 'react';
import type { StoreApi } from './useStore.ts';
import { Banner } from './components.tsx';
import { META } from '../plan/data.ts';
import { positionFor, dateForDayIndex } from '../plan/dates.ts';
import { phaseFor, isDeload, runWeekFor } from '../plan/phase.ts';
import { kindOf, sessionFor, weeklyRunningMinutes } from '../plan/session.ts';
import { effectiveSessionId, isOverridden, weekResetKeys,
         scheduleWarnings, LABELS } from '../plan/reschedule.ts';
import { dayProgress } from '../plan/progress.ts';
import { dayPlan, slotsFor, mealFor } from '../plan/nutrition.ts';
import { meanWeightForWeek, weighInCountForWeek, MIN_WEIGH_INS_FOR_MEAN } from '../plan/weights.ts';
import { trajectoryFor } from '../plan/derived.ts';

const DOW = ['M', 'T', 'W', 'T', 'F', 'S', 'S'];
const DOW_FULL = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

export function Week({ api }: { api: StoreApi }) {
  const { state } = api;
  const startDate = state.get<string>('setting:startDate') ?? META.defaultStartDate;
  const today = positionFor(startDate, new Date());
  const [week, setWeek] = useState(today.week);
  const [busy, setBusy] = useState(false);
  const movedDays = [0, 1, 2, 3, 4, 5, 6]
    .filter((dow) => isOverridden(state, (week - 1) * 7 + dow)).length;

  const phase = phaseFor(state, startDate, week);
  const target = trajectoryFor(week);
  const mean = meanWeightForWeek(state, startDate, week);
  const count = weighInCountForWeek(state, startDate, week);

  return (
    <>
      <div className="daynav">
        <button className="navbtn" aria-label="Previous week" disabled={week <= 1}
                onClick={() => setWeek((w) => Math.max(1, w - 1))}>‹</button>
        <div className="daynav-mid">
          <div className="daynav-day">Week {week}</div>
          <div className="daynav-sub">
            {dateForDayIndex(startDate, (week - 1) * 7)} — {dateForDayIndex(startDate, week * 7 - 1)}
            {' · '}Block {phase.eff}{isDeload(week) ? ' · de-load' : ''}
          </div>
        </div>
        <button className="navbtn" aria-label="Next week" disabled={week >= 16}
                onClick={() => setWeek((w) => Math.min(16, w + 1))}>›</button>
      </div>

      {phase.hold && (
        <Banner kind="warn" title={phase.forced ? 'Level opened anyway' : `Held at level ${phase.eff}`}>
          {phase.forced
            ? `Held ${phase.held} weeks, past the two-week limit. Working weights 10% lower.`
            : `Serving week ${runWeekFor(state, startDate, week)} rather than moving on.`}
        </Banner>
      )}

      <h2>Completion</h2>
      <div className="card">
        <div className="weekgrid">
          {[0, 1, 2, 3, 4, 5, 6].map((dow) => {
            const d = (week - 1) * 7 + dow;
            const p = dayProgress(state, startDate, d);
            const pct = p.total === 0 ? 0 : p.done / p.total;
            const isToday = d === today.dayIndex && !today.notStarted && !today.complete;
            return (
              <div className={`wday${isToday ? ' wday-today' : ''}`} key={dow}>
                <div className="wday-dow">{DOW[dow]}</div>
                <div className="wday-bar" aria-label={`${DOW_FULL[dow]}: ${p.done} of ${p.total}`}>
                  <div className="wday-fill" style={{ height: `${pct * 100}%` }} />
                </div>
                <div className="wday-num">{p.done}/{p.total}</div>
              </div>
            );
          })}
        </div>
      </div>

      <h2>Weight against the plan</h2>
      <div className="card">
        <div className="row">
          <span className="k">Trajectory target, end of week {week}</span>
          <span className="v">{target.weightKg.toFixed(2)} kg</span>
        </div>
        <div className="row">
          <span className="k">Your 7-day mean</span>
          <span className="v">
            {mean === null
              ? <span className="warn">unknown — {count} weigh-in{count === 1 ? '' : 's'} logged, {MIN_WEIGH_INS_FOR_MEAN} needed</span>
              : `${mean.toFixed(2)} kg`}
          </span>
        </div>
        {mean !== null && (
          <div className="row">
            <span className="k">Difference</span>
            <span className={`v ${mean <= target.weightKg ? 'ok' : 'warn'}`}>
              {mean <= target.weightKg ? '' : '+'}{(mean - target.weightKg).toFixed(2)} kg
            </span>
          </div>
        )}
        <div className="row">
          <span className="k">Running this week</span>
          <span className="v">{weeklyRunningMinutes(state, startDate, week)} min</span>
        </div>
      </div>

      <h2>Schedule</h2>
      <div className="card">
        <div className="row">
          <span className="k">Week {week}</span>
          <span className="v">
            {movedDays === 0
              ? <span className="ok">following the plan</span>
              : <span className="warn">{movedDays} day{movedDays === 1 ? '' : 's'} moved</span>}
          </span>
        </div>
        {scheduleWarnings(state, week).map((w) => <Banner kind="warn" key={w}>{w}</Banner>)}
        <p className="sub" style={{ marginBottom: movedDays ? 8 : 0 }}>
          To move a missed session, open <strong>Today</strong>, use the arrows to land on the
          day you want to train, and choose <strong>Move a missed session here</strong>.
        </p>
        {movedDays > 0 && (
          <div className="btns" style={{ marginTop: 0 }}>
            <button disabled={!api.durable || busy} onClick={async () => {
              if (!confirm(`Put week ${week} back on the plan? Ticks are not deleted — they `
                + `stay attached to the session they were made on, and reappear.`)) return;
              setBusy(true);
              try { for (const k of weekResetKeys(week)) if (state.has(k)) await api.del(k); }
              finally { setBusy(false); }
            }}>Reset week {week} to the plan</button>
          </div>
        )}
      </div>

      <h2>Sessions</h2>
      {[0, 1, 2, 3, 4, 5, 6].map((dow) => {
        const d = (week - 1) * 7 + dow;
        const entry = effectiveSessionId(state, d);
        const p = dayProgress(state, startDate, d);
        const movedDay = isOverridden(state, d);
        if (entry === 'rest') {
          return (
            <div className="card wsess" key={dow}>
              <div className="wsess-head">
                <span className="wsess-dow">{DOW_FULL[dow]}</span>
                <span className="wsess-count">{p.done}/{p.total}</span>
              </div>
              <div className="wsess-title">Rest — session missed</div>
              <div className="dim wsess-sub">
                Moved out of the way · {Math.round(dayPlan(state, d).k)} kcal planned
                across {slotsFor(state, d).length} slots
              </div>
            </div>
          );
        }
        const s = sessionFor(state, startDate, week, entry);
        const title = 'title' in s ? s.title : s.name;
        return (
          <div className="card wsess" key={dow}>
            <div className="wsess-head">
              <span className="wsess-dow">
                {DOW_FULL[dow]}{movedDay && <span className="movedtag">moved</span>}
              </span>
              <span className="wsess-count">{p.done}/{p.total}</span>
            </div>
            <div className="wsess-title">{title}</div>
            <div className="dim wsess-sub">
              {kindOf(entry) === 'run' && 'runningMinutes' in s
                ? `${s.runningMinutes} min running · ${s.totalTickTargets} blocks`
                : `${s.totalTickTargets} sets`}
              {' · '}{Math.round(dayPlan(state, d).k)} kcal planned across {slotsFor(state, d).length} slots
              {movedDay && ` · ${LABELS[entry]}`}
            </div>
          </div>
        );
      })}

      <h2>Food this week</h2>
      <div className="card">
        {[0, 1, 2, 3, 4, 5, 6].map((dow) => {
          const d = (week - 1) * 7 + dow;
          return (
            <details key={dow} className="wfood">
              <summary>
                <span>{DOW_FULL[dow]}</span>
                <span className="dim">{Math.round(dayPlan(state, d).k)} kcal · {Math.round(dayPlan(state, d).p)} g P</span>
              </summary>
              {slotsFor(state, d).map((slot) => (
                <div className="row" key={slot}>
                  <span className="k">{slot}</span>
                  <span className="v">{mealFor(state, d, slot).n}</span>
                </div>
              ))}
            </details>
          );
        })}
      </div>
    </>
  );
}
